import {
  DocumentTemplateType,
  TemplateDto,
  QueryFactory,
} from '@lmt/shared-types';

import { useUniDispatch } from '@lmt/unicorn-shared/app/store/types';
import { useModal } from '@lmt/unicorn-shared/app/components/common/ui-kit/modal/useModal';
import { convertToErrorString } from '@lmt/unicorn-shared/app/helpers/form/useErrorHandler';
import React, { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';

import { deleteTemplateMenuId } from './DeleteTemplateRichEditButton';
import { isDocumentNew, isDocumentType } from './richedit-helper';
import {
  CustomCommandExecutedEventArgs,
  DXR,
  RichEdit,
} from '../../../common/devexpress/RichEdit/dx-richedit';

export const activateTemplateButtonId = 'ActivateTemplateButtonId';

type Props = {
  richEdit: React.MutableRefObject<RichEdit>;
  templateType: DocumentTemplateType;
  currentTemplate: TemplateDto | null;
  setTemplate: (template: TemplateDto) => void;
};

/*
  Component that adds and handles "Activate/Deactivate" action for templates
 */
export const ActivateTemplateRichEditButton: React.FC<Props> = (props) => {
  const { richEdit, templateType, currentTemplate, setTemplate } = props;
  const dispatch = useUniDispatch();
  const modals = useModal();
  const i18n = useTranslation();
  const queryClient = useQueryClient();

  const activateTemplate = useCallback(async () => {
    if (!currentTemplate) {
      return;
    }
    const templateId = currentTemplate.id;
    try {
      const dto = { isActive: !currentTemplate.isActive };
      if (templateType == DocumentTemplateType.Document) {
        const template = await QueryFactory.DocumentTemplateClient.patch(
          templateId,
          dto,
        );
        setTemplate(template);
        await queryClient.invalidateQueries({
          queryKey: QueryFactory.DocumentTemplateQuery.getQueryKey(),
        });
        await queryClient.invalidateQueries({
          queryKey:
            QueryFactory.DocumentTemplateQuery.getSingleQueryKey(templateId),
        });
      }
    } catch (error) {
      await modals.showError({ text: convertToErrorString(error) });
    }
  }, [currentTemplate, templateType]);

  useEffect(
    function AddButtonsToRibbon() {
      const richEditCurrent = richEdit.current;
      if (currentTemplate && !isDocumentType(currentTemplate.type)) {
        richEditCurrent.updateRibbon((ribbon) => {
          if (!ribbon) return;
          const filePanel = ribbon.getTab(DXR.RibbonTabType.File)!;
          filePanel.removeItem(activateTemplateButtonId);
          if (
            currentTemplate.isDefault &&
            currentTemplate.title === 'gdt.templates.header'
          ) {
            return;
          }
          filePanel.insertItemBefore(
            new DXR.RibbonButtonItem(
              activateTemplateButtonId,
              currentTemplate.isActive
                ? i18n.t('documents.deactivate_template_button')
                : i18n.t('documents.activate_template_button'),
              {
                //icon: 'dxre-icon-ShowAllFieldResults',
                showText: true,
              },
            ),
            deleteTemplateMenuId,
          );
        });
      }
      return () => {
        if (
          (!currentTemplate || isDocumentType(currentTemplate.type)) &&
          richEdit.current?.isDisposed === false
        ) {
          richEditCurrent.updateRibbon((ribbon) => {
            if (!ribbon) return;
            const filePanel = ribbon.getTab(DXR.RibbonTabType.File)!;
            filePanel.removeItem(activateTemplateButtonId);
          });
        }
      };
    },
    [currentTemplate],
  );

  const updateActiveStatusHandler = useCallback(
    async (
      sender: RichEdit | undefined,
      args: CustomCommandExecutedEventArgs | undefined,
    ) => {
      if (!sender || !args) {
        return;
      }

      if (isDocumentNew(sender)) {
        return;
      }

      if (args.commandName === activateTemplateButtonId) {
        await activateTemplate();
      }
    },
    [activateTemplate],
  );

  useEffect(
    function AddHandlerForButtons() {
      richEdit.current.events.customCommandExecuted.addHandler(
        updateActiveStatusHandler,
      );
      return () => {
        richEdit.current.events.customCommandExecuted.removeHandler(
          updateActiveStatusHandler,
        );
      };
    },
    [updateActiveStatusHandler],
  );

  return null;
};
